// Cliente de la API del cotizador. Todo vive en SAP; el backend solo traduce.
import { api } from '@/services/api'
import type { DataResponse } from '@/types'

export type TaxCode = '16V' | '0V' | 'EV'
export type QuoteCurrency = 'MXP' | 'USD' | 'EUR'

export interface QuoteMeta {
  warehouses: string[]
  tax_codes: { code: TaxCode; label: string; rate: number }[]
  sales_person: { code: number; name: string } | null
  default_warehouse: string | null
}
export interface ItemHit { code: string; name: string; unit: string | null }
export interface CustomerHit {
  code: string; name: string; currency: string | null; pay_terms: number | null; rfc: string | null; email: string | null
}
export interface StockRow { warehouse: string; in_stock: number; committed: number; ordered: number; available: number }
export interface LastPrice {
  price: number; currency: string; doc_num: number; date: string; card_code: string; card_name: string; same_customer: boolean
}
export interface QuoteLineIn {
  item_code: string | null
  description: string
  quantity: number
  unit_price: number
  tax_code: TaxCode
  free_text?: string | null
}
export interface QuoteCreate {
  card_code: string
  warehouse: string
  currency: QuoteCurrency
  due_date: string
  comments?: string | null
  lines: QuoteLineIn[]
}
export interface QuoteLine {
  item_code: string | null; description: string | null; quantity: number | null; unit_price: number | null
  line_total: number | null; tax_code: string | null; warehouse: string | null; free_text: string | null
}
/** Cabecera de SAP normalizada (TotalDoc/VatDoc en la divisa del documento). */
export interface Quote {
  DocEntry: number
  DocNum: number
  DocDate: string
  DocDueDate: string
  CardCode: string
  CardName: string | null
  Currency: string
  DocRate: number
  TotalDoc: number
  VatDoc?: number
  DocumentStatus: 'bost_Open' | 'bost_Close'
  Comments: string | null
  SalesPersonCode: number
  DocumentLines?: QuoteLine[]
  pdf_url: string   // enlace firmado (1 h), se abre como liga normal
}

const get = async <T,>(url: string, params?: object) => (await api.get<DataResponse<T>>(url, { params })).data.data

export const getQuoteMeta = () => get<QuoteMeta>('/quotes/meta')
export const searchItems = (q: string) => get<ItemHit[]>('/quotes/items', { q })
export const searchCustomers = (q: string) => get<CustomerHit[]>('/quotes/customers', { q })
export const getItemStock = (code: string) => get<StockRow[]>(`/quotes/items/${encodeURIComponent(code)}/stock`)
export const getLastPrice = (code: string, card_code?: string) =>
  get<LastPrice | null>(`/quotes/items/${encodeURIComponent(code)}/last-price`, { card_code })
export interface QuoteFilters {
  seller?: number | ''     // solo admin; vacío = todos
  q?: string               // folio, cliente u observaciones
  date_from?: string
  date_to?: string
  status?: 'bost_Open' | 'bost_Close' | ''
  top?: number
}
export const listQuotes = (f: QuoteFilters = {}) =>
  get<Quote[]>('/quotes', Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null)))
export const listSellers = () => get<{ code: number; name: string }[]>('/quotes/sellers')
export const getQuote = (docNum: number) => get<Quote>(`/quotes/${docNum}`)
export async function createQuote(payload: QuoteCreate): Promise<Quote> {
  const { data } = await api.post<DataResponse<Quote>>('/quotes', payload)
  return data.data
}

export const CURRENCY_LABEL: Record<string, string> = { MXP: 'MXN', USD: 'USD', EUR: 'EUR' }
